import React, { useState, useEffect } from "react";
import { Phone, ArrowRight, Play, X } from "lucide-react";
import { CAKES, RESTAURANT } from "../mock";
import { videoStarted, videoStopped } from "../lib/mediaBus";

function VideoCard({ video }) {
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!playing) return;
    videoStarted();
    return () => videoStopped();
  }, [playing]);

  return (
    <div className="reveal group">
      <div className="rounded-2xl overflow-hidden bg-black border border-[#F3EAD7]/15 p-2">
        <div className="overflow-hidden rounded-xl relative">
          {playing ? (
            <>
              <iframe
                className="w-full h-40 md:h-52"
                src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&playsinline=1&modestbranding=1`}
                title={video.label}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
              <button
                onClick={() => setPlaying(false)}
                className="absolute top-2 right-2 z-20 w-8 h-8 rounded-full bg-black/70 hover:bg-[#D5521B] text-[#F3EAD7] flex items-center justify-center transition-colors"
                aria-label="Stop video"
                title="Close"
              >
                <X size={16} />
              </button>
            </>
          ) : (
            <button
              onClick={() => setPlaying(true)}
              className="relative block w-full h-40 md:h-52 focus:outline-none focus:ring-2 focus:ring-[#D5521B]"
              aria-label={`Play video: ${video.label}`}
            >
              <img
                src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
                alt={video.label}
                className="w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-opacity"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-14 h-14 rounded-full bg-[#D5521B] flex items-center justify-center shadow-[0_10px_28px_-6px_rgba(213,82,27,0.7)] transition-transform duration-300 group-hover:scale-110">
                  <Play size={24} className="text-[#FBF6EA] ml-0.5" fill="currentColor" />
                </span>
              </span>
            </button>
          )}
        </div>
        <div className="flex items-center justify-between px-1 pt-2.5">
          <span className="label-mono text-[#F0C766]">Video</span>
          <span className="label-mono text-[#7FB4C2]">{video.label}</span>
        </div>
      </div>
    </div>
  );
}

export default function Cakes() {
  return (
    <section id="cakes" className="py-24 md:py-32 bg-[#16110E] text-[#F3EAD7] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 reveal lg:sticky lg:top-28">
            <span className="label-mono text-[#F0C766]">{CAKES.eyebrow}</span>
            <h2 className="font-display font-black leading-[0.95] text-5xl md:text-6xl mt-4">
              {CAKES.title}
              <span className="block italic font-semibold text-[#F0C766]">
                {CAKES.titleAccent}
              </span>
            </h2>
            <p className="mt-6 text-[#D7CBB0] leading-relaxed">{CAKES.blurb}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold bg-[#F0C766] text-[#16110E] transition-transform duration-300 hover:-translate-y-0.5"
              >
                Contact Us <ArrowRight size={16} />
              </a>
              <a
                href={RESTAURANT.phoneHref}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border border-[#F3EAD7]/40 transition-colors duration-300 hover:bg-[#F3EAD7]/10"
              >
                <Phone size={15} /> Call to Order
              </a>
            </div>
            <p className="label-mono text-[#7FB4C2] mt-10">{CAKES.footnote}</p>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
            {CAKES.items.map((cake, i) => {
              return (
              <React.Fragment key={cake.no}>
              <div
                className="reveal group"
                style={{ transitionDelay: `${(i % 3) * 0.08}s` }}
              >
                <div className="rounded-2xl overflow-hidden bg-[#F3EAD7]/5 border border-[#F3EAD7]/15 p-2">
                  <div className="overflow-hidden rounded-xl">
                    <img
                      src={cake.img}
                      alt={cake.caption}
                      className="img-zoom w-full h-40 md:h-52 object-cover"
                    />
                  </div>
                  <div className="flex items-center justify-between px-1 pt-2.5">
                    <span className="label-mono text-[#F0C766]">{cake.no}</span>
                    <span className="label-mono text-[#7FB4C2]">Made to order</span>
                  </div>
                </div>
              </div>

              {/* Video 1: under № 07, beside № 10 */}
              {i === 8 && <VideoCard video={CAKES.video} />}

              {/* Video 2: right of № 10, under № 09 */}
              {i === 9 && <VideoCard video={CAKES.video2} />}
              </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
